// Copyright 2016 Sorama B.V.

import { AcquisitionServiceSettings, StorageInfo, VersionInfo } from "datatypes/SoramaLocalApi";

export enum ClientStatus {
    NotAvailable,
    Available,
    UnsupportedVersion,
    OutdatedVersion,
}

export enum FreeSpaceStatus {
    EnoughFreeSpace,
    CriticalFreeSpace,
    OutOfFreeSpace,
}

export type HardwareType = "dmadaq" | "dmaio";

export abstract class IClientService {
    readonly status: ClientStatus;
    readonly storageStatus: StorageInfo;
    readonly freeSpaceStatus: FreeSpaceStatus;
    readonly settings: AcquisitionServiceSettings;

    /**
     * Current storage path, convenience property to listen on changes
     */
    readonly storagePath: string;

    /**
     * Update the current status
     */
    abstract updateStatus(): Promise<ClientStatus>;

    /**
     * Updates the storage info
     */
    abstract updateStorage(): Promise<StorageInfo>;

    /**
     * Updates the settings
     * @param settings Optional parameter, if set the settings in the client are updated, otherwise the settings are retrieved from the client
     */
    abstract updateSettings(settings?: AcquisitionServiceSettings): Promise<AcquisitionServiceSettings>;

    /**
     * Get the currently installed Client version. Throws an error when CLient is not reachable.
     */
    abstract getInstalledVersion(): Promise<VersionInfo>;

    /**
     * Get the Version of the client currently online. THrows an error if the xml update file cannot be downloaded.
     */
    abstract getRemoteVersion(): Promise<VersionInfo>;

    /**
     * Returns true if the local version is different than the remote version.
     */
    abstract isUpdateAvailable(): Promise<boolean>;

    /**
     * Returns true if the local version is different than the remote version.
     * Setting setSkipOptionalUpdate on true will result in false, when isOptional is true.
     */
    abstract isUpdateRequired(): Promise<boolean>;

    /*
     * Gets the path to a folder on the users computer by using the Client to open a Folder Browser Dialog and returning the user's selection.
     */
    abstract getFolderPath(): Promise<string>;

    /*
     * Get the currently available firmware version
     */
    abstract getAvailableFirmwareVersion(hardwareType: HardwareType): Promise<VersionInfo>;

    /*
     * Initiates a call to the client to open the firmware update window
     */
    abstract openFirmwareUpdateWindow(): Promise<void>;
}
